import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  FileText,
  RefreshCw,
  Calendar,
  Search,
  Download,
  Eye,
  CreditCard,
  Receipt,
  AlertCircle,
  ArrowLeft,
  ArrowUpDown,
  User,
  Building2,
  Plus,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MoneyInput } from '../components/ui/money-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { PERMISSIONS } from '../constants/permissions';
import { useAuth } from '../context/AuthContext';
import customerCurrentAccountService, { CustomerTransaction, CreateTransactionPayload } from '../services/customerCurrentAccountService';
import customersService, { Customer } from '../services/clientsService';
import cashBoxService, { CashBox } from '../services/cashBoxService';
import LoadingScreen from '../components/ui/LoadingScreen';
import { formatCuit } from '../utils/format';
import CalendarModal from '../components/CalendarModal';
import { showError, showSuccess } from '../components/Alerts';
import { AtLimit } from '../components/ui/char-counter';

const ITEMS_PER_PAGE = 15;

type TransactionType = 'INGRESO' | 'PAGO' | 'CARGO';

const CustomerCurrentAccount: React.FC = () => {
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId: string }>();
  const { hasAnyPermission } = useAuth();
  const canOperate = hasAnyPermission(PERMISSIONS.CONTABILIDAD.COBRANZAS_OPERAR, PERMISSIONS.CONTABILIDAD.CUENTAS_CORRIENTES_VER);
  
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [transactions, setTransactions] = useState<CustomerTransaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<CustomerTransaction[]>([]);
  const [cashBoxes, setCashBoxes] = useState<CashBox[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('ALL');
  const [page, setPage] = useState(1);
  const [selectedTransaction, setSelectedTransaction] = useState<CustomerTransaction | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [addOperationOpen, setAddOperationOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState<{ startDate: Date | undefined; endDate: Date | undefined }>({
    startDate: undefined,
    endDate: undefined
  });
  const [startCalendarOpen, setStartCalendarOpen] = useState(false);
  const [endCalendarOpen, setEndCalendarOpen] = useState(false);

  // Sort state
  type SortColumn = 'createdAt' | 'type' | 'description' | 'amount';
  const [sortColumn, setSortColumn] = useState<SortColumn>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleSort = (col: SortColumn) => {
    if (sortColumn === col) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDirection('asc');
    }
  };

  // Form state para agregar operación
  const [formData, setFormData] = useState({
    type: 'INGRESO' as TransactionType,
    description: '',
    amount: '',
    cashBoxId: ''
  });

  // Estado para errores de validación
  const [validationError, setValidationError] = useState<string>('');

  // Estadísticas (balance y totales vienen del backend; el conteo se calcula desde filteredTransactions)
  const [stats, setStats] = useState({
    totalIncome: 0,
    totalExpense: 0,
    balance: 0,
  });

  // Cargar datos iniciales
  useEffect(() => {
    const loadInitialData = async () => {
      if (!customerId) {
        console.error('No customerId provided');
        return;
      }

      setLoading(true);
      try {
        // Cargar cliente
        const customerData = await customersService.getCustomerById(parseInt(customerId));
        setCustomer(customerData);

        // Cargar transacciones
        const transactionsData = await customerCurrentAccountService.getTransactionsByCustomer(parseInt(customerId));
        setTransactions(transactionsData);
        setFilteredTransactions(transactionsData);

        // Cargar balance de la cuenta corriente
        const accountData = await customerCurrentAccountService.getCustomerAccountBalance(parseInt(customerId));
        
        setStats({
          totalIncome: accountData.totalIncome,
          totalExpense: accountData.totalPayments,
          balance: accountData.balance,
        });

        // Cargar cajas
        const cashBoxesData = await cashBoxService.getAllCashBoxes();
        setCashBoxes(cashBoxesData);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [customerId]);

  const applyFilters = useCallback(() => {
    let filtered = [...transactions];

    // Filtro por tipo
    if (selectedType !== 'ALL') {
      filtered = filtered.filter(t => t.type === selectedType);
    }

    // Filtro por búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(t =>
        t.description.toLowerCase().includes(term) ||
        t.id.toString().includes(term) ||
        t.cashBoxName.toLowerCase().includes(term)
      );
    }

    // Filtro por fechas
    if (dateFilter.startDate) {
      filtered = filtered.filter(t =>
        new Date(t.createdAt) >= dateFilter.startDate!
      );
    }
    if (dateFilter.endDate) {
      const endOfDay = new Date(dateFilter.endDate!);
      endOfDay.setHours(23, 59, 59, 999);
      filtered = filtered.filter(t =>
        new Date(t.createdAt) <= endOfDay
      );
    }

    setFilteredTransactions(filtered);
  }, [transactions, selectedType, searchTerm, dateFilter]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Reset page on filter changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, selectedType, dateFilter]);

  const getTransactionIcon = (type: TransactionType) => {
    switch (type) {
      case 'INGRESO':
        return <CreditCard className="w-5 h-5" />;
      case 'PAGO':
        return <Receipt className="w-5 h-5" />;
      case 'CARGO':
        return <AlertCircle className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const getTransactionTypeName = (type: TransactionType) => {
    switch (type) {
      case 'INGRESO': return 'Ingreso';
      case 'PAGO':    return 'Pago';
      case 'CARGO':   return 'Cargo';
      default:        return type;
    }
  };

  const formatCurrency = (amount: number) => {
    return `$ ${Math.abs(amount).toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewDetails = (transaction: CustomerTransaction) => {
    setSelectedTransaction(transaction);
    setDetailsOpen(true);
  };

  const handleFormChange = (field: string, value: string) => {
    // Limpiar error al modificar el formulario
    if (validationError) {
      setValidationError('');
    }

    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // Limpiar cashBoxId solo para CARGO (no necesita caja); INGRESO y PAGO sí la necesitan
      if (field === 'type' && value === 'CARGO') {
        newData.cashBoxId = '';
      }
      
      return newData;
    });
  };

  const handleSubmitOperation = async () => {
    // Limpiar errores previos
    setValidationError('');

    // Validar campos requeridos
    if (!customerId || !formData.amount || !formData.description) {
      setValidationError('Por favor complete todos los campos requeridos');
      return;
    }

    // INGRESO y PAGO requieren caja; CARGO no
    if ((formData.type === 'INGRESO' || formData.type === 'PAGO') && !formData.cashBoxId) {
      setValidationError('Por favor seleccione una caja');
      return;
    }

    const operationAmount = parseFloat(formData.amount);
    if ((formData.type === 'PAGO' || formData.type === 'CARGO') && operationAmount > stats.balance) {
      setValidationError(`Saldo insuficiente. Saldo actual: $${stats.balance.toLocaleString('es-AR')}, monto requerido: $${operationAmount.toLocaleString('es-AR')}`);
      return;
    }

    try {
      setLoading(true);
      const payload: CreateTransactionPayload = {
        customerId: parseInt(customerId),
        cashBoxId: formData.type !== 'CARGO' ? parseInt(formData.cashBoxId) : null,
        type: formData.type,
        description: formData.description,
        amount: parseFloat(formData.amount)
      };

      await customerCurrentAccountService.createTransaction(payload);

      const [transactionsData, accountData] = await Promise.all([
        customerCurrentAccountService.getTransactionsByCustomer(parseInt(customerId)),
        customerCurrentAccountService.getCustomerAccountBalance(parseInt(customerId)),
      ]);
      setTransactions(transactionsData);
      setFilteredTransactions(transactionsData);
      setStats({
        totalIncome: accountData.totalIncome,
        totalExpense: accountData.totalPayments,
        balance: accountData.balance,
      });

      setFormData({ type: 'INGRESO', description: '', amount: '', cashBoxId: '' });
      setValidationError('');
      setAddOperationOpen(false);
      showSuccess('Operación registrada correctamente');
    } catch (error: any) {
      console.error('Error creating transaction:', error);
      const msg = error.response?.data?.message || error.message || 'Error desconocido';
      showError(`Error al crear la transacción: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!customer) return;
    
    const customerName = customer.type === 'PERSON' 
      ? `${customer.firstName} ${customer.lastName}`
      : customer.businessName;
    
    const headers = ['ID', 'Fecha', 'Tipo', 'Caja', 'Descripción', 'Monto'];
    const rows = filteredTransactions.map(t => [
      t.id,
      formatDate(t.createdAt),
      getTransactionTypeName(t.type),
      t.cashBoxName,
      t.description,
      t.amount
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `cuenta_corriente_${customerName.replace(/\s/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _renderTransactionDetails = (transaction: CustomerTransaction) => {
    return (
      <div className="space-y-4">
        <div className="p-4 rounded-lg bg-gray-50">
          <p className="mb-2"><span className="font-medium">Descripción:</span> {transaction.description}</p>
          <p><span className="font-medium">Caja:</span> {transaction.cashBoxName}</p>
        </div>
        
        <div className="pt-4 border-t">
          <h4 className="mb-3 font-semibold">Información Adicional</h4>
          <div className="space-y-2">
            <p><span className="font-medium">Tipo:</span> {getTransactionTypeName(transaction.type)}</p>
            <p><span className="font-medium">Monto:</span> {formatCurrency(transaction.amount)}</p>
            <p><span className="font-medium">Fecha:</span> {formatDate(transaction.createdAt)}</p>
          </div>
        </div>
      </div>
    );
  };

  const getCustomerDisplayName = () => {
    if (!customer) return '';
    return customer.type === 'PERSON' 
      ? `${customer.firstName} ${customer.lastName}`
      : customer.businessName;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _getCustomerIcon = () => {
    if (!customer) return <User className="w-8 h-8" />;
    return customer.type === 'PERSON' 
      ? <User className="w-8 h-8" />
      : <Building2 className="w-8 h-8" />;
  };

  if (loading && !customer) {
    return <LoadingScreen message="Cargando cuenta corriente..." />;
  }

  const isCompany = customer?.type === 'COMPANY';

  return (
    <div className="w-full h-full p-6 space-y-6">

      {/* Breadcrumb + Back */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>
        <span className="text-muted-foreground text-sm">/</span>
        <span className="text-sm font-medium text-foreground">
          {getCustomerDisplayName() || 'Cuenta Corriente'}
        </span>
      </div>

      {/* Main card */}
      <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">

        {/* Header */}
        <div className="px-6 py-5 border-b border-border">
          <div className="flex flex-col items-start justify-between gap-4 mb-6 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4">
              <div className={`flex-shrink-0 p-3 rounded-xl ${
                isCompany
                  ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                  : 'bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400'
              }`}>
                {isCompany ? <Building2 className="w-6 h-6" /> : <User className="w-6 h-6" />}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  {getCustomerDisplayName() || '—'}
                </h1>
                <div className="flex items-center gap-3 mt-1">
                  {customer && (
                    <span className="font-mono text-xs text-muted-foreground">
                      {formatCuit(customer.cuit)}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {isCompany ? 'Empresa' : 'Persona Física'}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={exportToCSV}
                className="flex items-center gap-2 rounded-lg"
              >
                <Download className="w-4 h-4" />
                Exportar
              </Button>
              {canOperate && (
                <Button
                  size="sm"
                  onClick={() => setAddOperationOpen(true)}
                  className="flex items-center gap-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90"
                >
                  <Plus className="w-4 h-4" />
                  Nueva Operación
                </Button>
              )}
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {/* Saldo */}
            <div className="flex items-center gap-3 p-4 border rounded-lg border-border bg-background">
              <div className="flex-shrink-0 p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
                <DollarSign className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase text-muted-foreground">Saldo</p>
                <p className={`text-lg font-bold ${
                  stats.balance >= 0 ? 'text-foreground' : 'text-destructive'
                }`}>
                  ${stats.balance.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
            {/* Ingresos */}
            <div className="flex items-center gap-3 p-4 border rounded-lg border-border bg-background">
              <div className="flex-shrink-0 p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase text-muted-foreground">Ingresos</p>
                <p className="text-lg font-bold text-foreground">
                  ${stats.totalIncome.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
            {/* Pagos */}
            <div className="flex items-center gap-3 p-4 border rounded-lg border-border bg-background">
              <div className="flex-shrink-0 p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                <TrendingDown className="w-4 h-4 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase text-muted-foreground">Pagos</p>
                <p className="text-lg font-bold text-foreground">
                  ${stats.totalExpense.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
            {/* Movimientos */}
            <div className="flex items-center gap-3 p-4 border rounded-lg border-border bg-background">
              <div className="flex-shrink-0 p-2 rounded-lg bg-muted">
                <FileText className="w-4 h-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase text-muted-foreground">Movimientos</p>
                <p className="text-lg font-bold text-foreground">{filteredTransactions.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters toolbar */}
        <div className="flex flex-col items-end gap-4 px-6 py-4 border-b border-border md:flex-row">
          <div className="flex flex-wrap gap-4">
            {/* Tipo */}
            <div className="space-y-2">
              <label className="text-xs font-medium uppercase text-muted-foreground">Tipo</label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="w-[160px] rounded-lg">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos los tipos</SelectItem>
                  <SelectItem value="INGRESO">Ingreso</SelectItem>
                  <SelectItem value="PAGO">Pago</SelectItem>
                  <SelectItem value="CARGO">Cargo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Desde */}
            <div className="space-y-2">
              <label className="text-xs font-medium uppercase text-muted-foreground">Desde</label>
              <button
                onClick={() => setStartCalendarOpen(true)}
                className="flex items-center gap-2 px-3 h-9 w-[160px] rounded-lg border border-input bg-background text-sm transition-colors hover:bg-accent text-left"
              >
                <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className={dateFilter.startDate ? 'text-foreground' : 'text-muted-foreground'}>
                  {dateFilter.startDate
                    ? dateFilter.startDate.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })
                    : 'Desde...'}
                </span>
                {dateFilter.startDate && (
                  <span
                    role="button"
                    onClick={(e) => { e.stopPropagation(); setDateFilter(f => ({ ...f, startDate: undefined })); }}
                    className="ml-auto text-muted-foreground hover:text-foreground text-xs leading-none"
                  >
                    ✕
                  </span>
                )}
              </button>
              <CalendarModal
                open={startCalendarOpen}
                onOpenChange={setStartCalendarOpen}
                selectedDate={dateFilter.startDate}
                onSelectDate={(date) => setDateFilter(f => ({ ...f, startDate: date }))}
              />
            </div>
            {/* Hasta */}
            <div className="space-y-2">
              <label className="text-xs font-medium uppercase text-muted-foreground">Hasta</label>
              <button
                onClick={() => setEndCalendarOpen(true)}
                className="flex items-center gap-2 px-3 h-9 w-[160px] rounded-lg border border-input bg-background text-sm transition-colors hover:bg-accent text-left"
              >
                <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className={dateFilter.endDate ? 'text-foreground' : 'text-muted-foreground'}>
                  {dateFilter.endDate
                    ? dateFilter.endDate.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })
                    : 'Hasta...'}
                </span>
                {dateFilter.endDate && (
                  <span
                    role="button"
                    onClick={(e) => { e.stopPropagation(); setDateFilter(f => ({ ...f, endDate: undefined })); }}
                    className="ml-auto text-muted-foreground hover:text-foreground text-xs leading-none"
                  >
                    ✕
                  </span>
                )}
              </button>
              <CalendarModal
                open={endCalendarOpen}
                onOpenChange={setEndCalendarOpen}
                selectedDate={dateFilter.endDate}
                onSelectDate={(date) => setDateFilter(f => ({ ...f, endDate: date }))}
              />
            </div>
          </div>
          {/* Search */}
          <div className="w-full space-y-2 md:w-72 md:ml-auto">
            <label className="text-xs font-medium uppercase text-muted-foreground">Buscar</label>
            <div className="relative">
              <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
              <Input
                placeholder="Descripción, caja..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="rounded-lg pl-9 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </div>
          </div>
        </div>

        {/* Transactions table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-medium text-foreground">Sin movimientos</h3>
              <p className="mb-4 text-sm text-muted-foreground">No hay transacciones registradas para este cliente</p>
              {canOperate && (
                <Button
                  size="sm"
                  onClick={() => setAddOperationOpen(true)}
                  className="bg-primary text-primary-foreground hover:opacity-90"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Operación
                </Button>
              )}
            </div>
          ) : (() => {
            const sortedTransactions = [...filteredTransactions].sort((a, b) => {
              let cmp = 0;
              switch (sortColumn) {
                case 'createdAt': cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(); break;
                case 'type': cmp = a.type.localeCompare(b.type); break;
                case 'description': cmp = a.description.localeCompare(b.description); break;
                case 'amount': cmp = a.amount - b.amount; break;
              }
              return sortDirection === 'asc' ? cmp : -cmp;
            });
            const totalPages = Math.ceil(sortedTransactions.length / ITEMS_PER_PAGE);
            const safePage = Math.min(page, Math.max(1, totalPages));
            const pageItems = sortedTransactions.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

            return (
              <>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="px-5 py-3 text-left">
                        <button
                          onClick={() => handleSort('createdAt')}
                          className="flex items-center gap-2 text-sm font-medium transition-colors text-muted-foreground hover:text-foreground"
                        >
                          Fecha
                          <ArrowUpDown className={`w-4 h-4 ${sortColumn === 'createdAt' ? 'text-primary' : ''}`} />
                        </button>
                      </th>
                      <th className="px-5 py-3 text-left">
                        <button
                          onClick={() => handleSort('type')}
                          className="flex items-center gap-2 text-sm font-medium transition-colors text-muted-foreground hover:text-foreground"
                        >
                          Tipo
                          <ArrowUpDown className={`w-4 h-4 ${sortColumn === 'type' ? 'text-primary' : ''}`} />
                        </button>
                      </th>
                      <th className="px-5 py-3 text-left">
                        <button
                          onClick={() => handleSort('description')}
                          className="flex items-center gap-2 text-sm font-medium transition-colors text-muted-foreground hover:text-foreground"
                        >
                          Descripción
                          <ArrowUpDown className={`w-4 h-4 ${sortColumn === 'description' ? 'text-primary' : ''}`} />
                        </button>
                      </th>
                      <th className="px-5 py-3 text-left">
                        <span className="text-sm font-medium text-muted-foreground">Caja</span>
                      </th>
                      <th className="px-5 py-3 text-right">
                        <button
                          onClick={() => handleSort('amount')}
                          className="flex items-center gap-2 text-sm font-medium transition-colors text-muted-foreground hover:text-foreground ml-auto"
                        >
                          Monto
                          <ArrowUpDown className={`w-4 h-4 ${sortColumn === 'amount' ? 'text-primary' : ''}`} />
                        </button>
                      </th>
                      <th className="px-5 py-3 text-center">
                        <span className="text-sm font-medium text-muted-foreground">Detalle</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((transaction, index) => {
                      const isLast = index === pageItems.length - 1;
                      const cell = isLast ? 'px-5 py-3' : 'px-5 py-3 border-b border-border';
                      const isIngreso = transaction.type === 'INGRESO';
                      return (
                        <tr key={transaction.id} className="transition-colors hover:bg-muted/30">
                          <td className={cell}>
                            <span className="text-sm text-foreground">{formatDate(transaction.createdAt)}</span>
                          </td>
                          <td className={cell}>
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                              transaction.type === 'INGRESO'
                                ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800/30'
                                : transaction.type === 'PAGO'
                                ? 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800/30'
                                : 'bg-destructive/10 text-destructive border-destructive/20'
                            }`}>
                              {getTransactionIcon(transaction.type)}
                              {getTransactionTypeName(transaction.type)}
                            </span>
                          </td>
                          <td className={cell}>
                            <span className="text-sm text-foreground max-w-[200px] truncate block">
                              {transaction.description}
                            </span>
                          </td>
                          <td className={cell}>
                            <span className="text-sm text-muted-foreground">
                              {transaction.cashBoxName || '—'}
                            </span>
                          </td>
                          <td className={`${cell} text-right`}>
                            <span className={`text-sm font-semibold ${
                              isIngreso ? 'text-green-600 dark:text-green-400' : 'text-destructive'
                            }`}>
                              {isIngreso ? '+' : '−'}{formatCurrency(transaction.amount)}
                            </span>
                          </td>
                          <td className={`${cell} text-center`}>
                            <button
                              onClick={() => handleViewDetails(transaction)}
                              className="inline-flex items-center justify-center p-1.5 rounded-lg transition-colors hover:bg-accent text-muted-foreground hover:text-foreground"
                              title="Ver detalle"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Footer with pagination */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Mostrando{' '}
                    <span className="font-semibold text-foreground">{pageItems.length}</span> de{' '}
                    <span className="font-semibold text-foreground">{sortedTransactions.length}</span>{' '}
                    movimiento{sortedTransactions.length !== 1 ? 's' : ''}
                  </p>
                  {totalPages > 1 && (
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={safePage === 1}>
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-foreground">{safePage} / {totalPages}</span>
                      <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={safePage === totalPages}>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              {selectedTransaction && getTransactionIcon(selectedTransaction.type)}
              Transacción #{selectedTransaction?.id}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {selectedTransaction && getTransactionTypeName(selectedTransaction.type)}
            </DialogDescription>
          </DialogHeader>
          {selectedTransaction && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-muted/50">
                <div>
                  <p className="text-xs font-medium uppercase text-muted-foreground mb-1">Monto</p>
                  <p className={`text-xl font-bold ${
                    selectedTransaction.type === 'INGRESO' ? 'text-green-600' : 'text-destructive'
                  }`}>
                    {selectedTransaction.type === 'INGRESO' ? '+' : '−'}{formatCurrency(selectedTransaction.amount)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-muted-foreground mb-1">Fecha</p>
                  <p className="text-sm text-foreground">{formatDate(selectedTransaction.createdAt)}</p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium uppercase text-muted-foreground mb-1">Descripción</p>
                  <p className="text-sm text-foreground">{selectedTransaction.description}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-muted-foreground mb-1">Caja</p>
                  <p className="text-sm text-foreground">{selectedTransaction.cashBoxName || '—'}</p>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={() => setDetailsOpen(false)} className="rounded-lg">Cerrar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Operation Dialog */}
      <Dialog open={addOperationOpen} onOpenChange={(open) => {
        setAddOperationOpen(open);
        if (!open) setValidationError('');
      }}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl font-bold text-foreground">
              <Plus className="w-5 h-5 text-indigo-600" />
              Nueva Operación
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Registrar un movimiento en la cuenta corriente
            </DialogDescription>
          </DialogHeader>

          {validationError && (
            <div className="flex items-start gap-2 p-3 text-sm rounded-lg bg-destructive/10 border border-destructive/20 text-destructive">
              <AlertCircle className="flex-shrink-0 w-4 h-4 mt-0.5" />
              <p>{validationError}</p>
            </div>
          )}

          <div className="grid gap-5 py-2">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">
                Tipo de Operación
              </Label>
              <Select value={formData.type} onValueChange={(v) => handleFormChange('type', v)}>
                <SelectTrigger className="rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INGRESO">
                    <span className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-green-600" />
                      Ingreso
                    </span>
                  </SelectItem>
                  <SelectItem value="PAGO">
                    <span className="flex items-center gap-2">
                      <Receipt className="w-4 h-4 text-blue-600" />
                      Pago
                    </span>
                  </SelectItem>
                  <SelectItem value="CARGO">
                    <span className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-destructive" />
                      Cargo
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(formData.type === 'INGRESO' || formData.type === 'PAGO') && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">
                  Caja{formData.type === 'INGRESO' ? ' destino' : ' origen'}
                </Label>
                <Select value={formData.cashBoxId} onValueChange={(v) => handleFormChange('cashBoxId', v)}>
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="Seleccionar caja..." />
                  </SelectTrigger>
                  <SelectContent>
                    {cashBoxes.map((cb) => (
                      <SelectItem key={cb.id} value={String(cb.id)}>
                        {cb.name} — {cb.bank} ({cb.currency})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">Monto</Label>
              <MoneyInput
                currency="ARS"
                value={formData.amount === '' ? undefined : Number(formData.amount)}
                onValueChange={(v) => handleFormChange('amount', v !== null ? String(v) : '')}
                className="rounded-lg bg-background border-input"
                placeholder="0,00"
              />
              <p className="text-xs text-muted-foreground">
                {formData.type === 'INGRESO' ? 'Se sumará al saldo' : 'Se descontará del saldo'}
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">Descripción</Label>
              <Input
                type="text"
                placeholder="Ej: Pago de factura #1234"
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                maxLength={255}
                className="rounded-lg bg-background border-input"
              />
              <AtLimit value={formData.description} max={255} />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setAddOperationOpen(false)} className="rounded-lg">
              Cancelar
            </Button>
            <Button
              onClick={handleSubmitOperation}
              disabled={loading}
              className="rounded-lg bg-primary text-primary-foreground hover:opacity-90"
            >
              {loading ? 'Guardando...' : 'Guardar Operación'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerCurrentAccount;
